<template>
  <div class="companies-container">
    <!-- TOP VARIANCE CHARTS SECTION -->
    <div v-if="subcontractors.length > 0" class="variance-charts-section">
      <div class="variance-charts-row">
        <!-- Top 5 Work Category Variances -->
        <div class="variance-chart-card">
          <h3>Top 5 Work Category Variances</h3>
          <div class="variance-bars">
            <div v-for="(cat, idx) in topCategoryVariances" :key="'cat-' + idx" class="variance-bar-item">
              <div class="bar-label">{{ cat.name }}</div>
              <div class="bar-container">
                <div class="bar-original" :style="{ width: getCategoryOriginalWidth(cat) }">
                  {{ currencyFormatShort(cat.original) }}
                </div>
                <div 
                  v-if="cat.variance > 0"
                  class="bar-variance positive" 
                  :style="{ width: getCategoryVarianceWidth(cat) }"
                >
                  +{{ percentFormat(cat.variancePct) }}
                </div>
              </div>
              <div class="bar-total">{{ currencyFormatShort(cat.current) }}</div>
            </div>
          </div>
        </div>
        
        <!-- Top 5 Contractor Variances -->
        <div class="variance-chart-card">
          <h3>Top 5 Contractor Variances</h3>
          <div class="variance-bars">
            <div v-for="(con, idx) in topContractorVariances" :key="'con-' + idx" class="variance-bar-item">
              <div class="bar-label">{{ truncateName(con.name, 25) }}</div>
              <div class="bar-container">
                <div class="bar-original" :style="{ width: getContractorOriginalWidth(con) }">
                  {{ currencyFormatShort(con.original) }}
                </div>
                <div 
                  v-if="con.variance > 0"
                  class="bar-variance positive" 
                  :style="{ width: getContractorVarianceWidth(con) }"
                >
                  +{{ percentFormat(con.variancePct) }}
                </div>
              </div>
              <div class="bar-total">{{ currencyFormatShort(con.current) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="company-header-container">
      <div class="company-header-title">Subcontractor Performance</div>
      <div class="company-header-subtitle">
        View performance of trades within your comp set based on G702/G703 payment applications.
      </div>
      
      <!-- Search and filter controls -->
      <div class="company-search-controls">
        <input 
          type="text" 
          v-model="companyNameFilter" 
          placeholder="Search by Company Name" 
          class="company-search-input"
        />
        <select v-model="scopeOfWorkFilter" class="scope-filter">
          <option value="">All Scopes of Work</option>
          <option v-for="scope in availableScopes" :key="scope" :value="scope">
            {{ scope }}
          </option>
        </select>
        <select v-model="sortOrder" class="sort-order">
          <option value="budget_desc">Sort by Budget Variance (High to Low)</option>
          <option value="budget_asc">Sort by Budget Variance (Low to High)</option>
          <option value="schedule_desc">Sort by Schedule Variance (High to Low)</option>
          <option value="schedule_asc">Sort by Schedule Variance (Low to High)</option>
        </select>
      </div>
    </div>
    
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <div>Loading subcontractor data...</div>
    </div>
    
    <div v-else-if="filteredSubcontractors.length === 0" class="no-data-container">
      <div v-if="companyNameFilter || scopeOfWorkFilter">
        No subcontractors match your filter criteria. Try adjusting your filters.
      </div>
      <div v-else class="no-data-message">
        <h3>📋 No Contractor Performance Data Available</h3>
        <p>Contractor performance data comes from uploaded G702/G703 documents.</p>
        <div class="help-text">
          <p><strong>To see contractor data here:</strong></p>
          <ol>
            <li>Navigate to individual project pages</li>
            <li>Upload G702/G703 payment application documents</li>
            <li>Process the documents to extract contractor information</li>
            <li>Return to comp search to view aggregated contractor performance</li>
          </ol>
        </div>
        <p class="note">Once G703 documents are uploaded and processed, you'll see contractor budget variance, completion percentages, and performance metrics here.</p>
      </div>
    </div>
    
    <!-- Football field charts for subcontractors -->
    <div v-else class="football-field-charts">
      <div v-for="(subcontractor, index) in filteredSubcontractors" :key="index" class="subcontractor-chart">
        <div class="subcontractor-header">
          <h3>{{ subcontractor.company_name }}</h3>
          <div class="scope-tag">{{ subcontractor.scope_of_work }}</div>
        </div>
        
        <!-- Budget variance chart -->
        <div class="variance-chart budget-variance">
          <div class="chart-label">Budget Variance</div>
          <div class="football-field">
            <div class="original-value" :style="{ width: getOriginalWidth(subcontractor) }">
              {{ currencyFormat(subcontractor.original_budget) }}
            </div>
            <div 
              v-if="subcontractor.budget_variance !== 0"
              class="variance-value" 
              :class="{ 'positive': subcontractor.budget_variance > 0, 'negative': subcontractor.budget_variance < 0 }"
              :style="{ width: getVarianceWidth(subcontractor) }"
            >
              {{ formatVariancePercent(subcontractor.budget_variance_percent) }}
            </div>
          </div>
        </div>
        
        <!-- Contract details -->
        <div class="contract-details">
          <div class="detail-item">
            <span class="detail-label">Original Contract:</span>
            <span class="detail-value">{{ currencyFormat(subcontractor.original_budget) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Budget Increases:</span>
            <span class="detail-value">{{ currencyFormat(subcontractor.budget_increases) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Current Budget:</span>
            <span class="detail-value">{{ currencyFormat(subcontractor.current_budget) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useCompSetStore } from "@/stores/comp_set_store";
import { useAuthStore } from "@/stores/auth_store";
import { useErrorStore } from "@/stores/error_store";
import { axiosQuery, currencyFormat } from "@/assets/js/utils";

// Store references
const compSetStore = useCompSetStore();
const authStore = useAuthStore();
const errorStore = useErrorStore();

// Data refs
const subcontractors = ref([]);
const companyNameFilter = ref('');
const scopeOfWorkFilter = ref('');
const sortOrder = ref('budget_desc');
const availableScopes = ref([]);
const isLoading = ref(true);

// API base URL
const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/";
let authToken = ref({});

// Helper functions for bar chart widths
function getOriginalWidth(subcontractor) {
  // If no variance, original takes full width
  if (subcontractor.budget_variance === 0) return '100%';
  // Otherwise proportional based on current budget
  const total = subcontractor.current_budget || subcontractor.original_budget;
  if (total === 0) return '100%';
  const ratio = (subcontractor.original_budget / total) * 100;
  return `${Math.min(Math.max(ratio, 20), 85)}%`; // Keep between 20-85%
}

function getVarianceWidth(subcontractor) {
  const total = subcontractor.current_budget || subcontractor.original_budget;
  if (total === 0) return '0%';
  const absVariance = Math.abs(subcontractor.budget_variance);
  const ratio = (absVariance / total) * 100;
  return `${Math.min(Math.max(ratio, 10), 50)}%`; // Keep between 10-50%
}

function formatVariancePercent(pct) {
  const percent = Math.abs(pct * 100).toFixed(1);
  return pct >= 0 ? `+${percent}%` : `-${percent}%`;
}

// Helper for short currency format
function currencyFormatShort(value) {
  if (value >= 1000000) {
    return '$' + (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return '$' + (value / 1000).toFixed(0) + 'K';
  }
  return currencyFormat(value);
}

function percentFormat(value) {
  return (value * 100).toFixed(1) + '%';
}

function truncateName(name, maxLength) {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + '...';
}

// Helper to strip trade codes from scope names (e.g., "005-036 - Plumbing" -> "Plumbing")
function cleanScopeName(scope) {
  if (!scope) return 'Other';
  // Strip patterns like "005-036 - ", "L07043-00065 - ", "U00030-001.3 - " etc.
  return scope.replace(/^[A-Z0-9\-\.]+\s*-\s*/i, '').trim() || scope;
}

// Top 5 Work Category Variances (grouped by cleaned scope_of_work)
const topCategoryVariances = computed(() => {
  const categoryMap = {};
  
  subcontractors.value.forEach(s => {
    // Clean the scope name by stripping trade codes
    const rawScope = s.scope_of_work || 'Other';
    const cleanedScope = cleanScopeName(rawScope);
    
    if (!categoryMap[cleanedScope]) {
      categoryMap[cleanedScope] = { name: cleanedScope, original: 0, current: 0, variance: 0 };
    }
    categoryMap[cleanedScope].original += s.original_budget || 0;
    categoryMap[cleanedScope].current += s.current_budget || 0;
  });
  
  // Calculate variance for each category
  Object.values(categoryMap).forEach(cat => {
    cat.variance = cat.current - cat.original;
    cat.variancePct = cat.original > 0 ? cat.variance / cat.original : 0;
  });
  
  // Sort by variance amount (highest first) and take top 5
  return Object.values(categoryMap)
    .filter(c => c.variance > 0)
    .sort((a, b) => b.variance - a.variance)
    .slice(0, 5);
});

// Top 5 Contractor Variances (aggregated by contractor name to avoid duplicates)
const topContractorVariances = computed(() => {
  const contractorMap = {};
  
  // Aggregate by contractor name
  subcontractors.value.forEach(s => {
    const name = s.company_name || 'Unknown';
    if (!contractorMap[name]) {
      contractorMap[name] = { name, original: 0, current: 0, variance: 0 };
    }
    contractorMap[name].original += s.original_budget || 0;
    contractorMap[name].current += s.current_budget || 0;
  });
  
  // Calculate variance for each contractor
  Object.values(contractorMap).forEach(con => {
    con.variance = con.current - con.original;
    con.variancePct = con.original > 0 ? con.variance / con.original : 0;
  });
  
  // Sort by variance amount (highest first) and take top 5
  return Object.values(contractorMap)
    .filter(c => c.variance > 0)
    .sort((a, b) => b.variance - a.variance)
    .slice(0, 5);
});

// Width calculators for category bars
function getCategoryOriginalWidth(cat) {
  if (cat.variance <= 0) return '100%';
  const total = cat.current;
  if (total === 0) return '100%';
  const ratio = (cat.original / total) * 100;
  return `${Math.min(Math.max(ratio, 30), 80)}%`;
}

function getCategoryVarianceWidth(cat) {
  const total = cat.current;
  if (total === 0) return '0%';
  const ratio = (cat.variance / total) * 100;
  return `${Math.min(Math.max(ratio, 15), 50)}%`;
}

// Width calculators for contractor bars
function getContractorOriginalWidth(con) {
  if (con.variance <= 0) return '100%';
  const total = con.current;
  if (total === 0) return '100%';
  const ratio = (con.original / total) * 100;
  return `${Math.min(Math.max(ratio, 30), 80)}%`;
}

function getContractorVarianceWidth(con) {
  const total = con.current;
  if (total === 0) return '0%';
  const ratio = (con.variance / total) * 100;
  return `${Math.min(Math.max(ratio, 15), 50)}%`;
}

// Computed properties
const filteredSubcontractors = computed(() => {
  let result = subcontractors.value;
  
  // Apply company name filter
  if (companyNameFilter.value) {
    result = result.filter(s => 
      s.company_name.toLowerCase().includes(companyNameFilter.value.toLowerCase())
    );
  }
  
  // Apply scope of work filter
  if (scopeOfWorkFilter.value) {
    result = result.filter(s => s.scope_of_work === scopeOfWorkFilter.value);
  }
  
  // Apply sorting
  result = result.sort((a, b) => {
    if (sortOrder.value === 'budget_desc') {
      return b.budget_variance_percent - a.budget_variance_percent;
    } else if (sortOrder.value === 'budget_asc') {
      return a.budget_variance_percent - b.budget_variance_percent;
    } else if (sortOrder.value === 'schedule_desc') {
      return b.schedule_variance_percent - a.schedule_variance_percent;
    } else if (sortOrder.value === 'schedule_asc') {
      return a.schedule_variance_percent - b.schedule_variance_percent;
    }
    return 0;
  });
  
  return result;
});

// Fetch subcontractor data using the efficient contractor performance endpoint
async function fetchSubcontractorData() {
  isLoading.value = true;
  const selectedProjectIds = Object.keys(compSetStore.selectedProjects)
    .filter(id => compSetStore.selectedProjects[id]);
    
  if (selectedProjectIds.length === 0) {
    subcontractors.value = [];
    isLoading.value = false;
    return;
  }
  
  console.log(`🔥 Fetching contractor performance for ${selectedProjectIds.length} projects using efficient endpoint`);
  
  // Use the NEW efficient endpoint that returns LATEST draw per contractor across all projects in ONE call
  const url = `${apiBase}projects/fetch_contractor_performance?project_ids=${selectedProjectIds.join(',')}`;
  console.log(`🔥 Calling contractor performance endpoint:`, url);
  
  axiosQuery(
    url,
    authToken.value,
    (data) => {
      console.log(`📊 Contractor performance data received:`, data);
      
      const contractors = data.contractors || [];
      console.log(`✅ Received ${contractors.length} contractors (latest draw per contractor) across ${data.total_projects} projects`);
      
      if (contractors.length === 0) {
        console.log('⚠️ No contractor data available');
        subcontractors.value = [];
        availableScopes.value = [];
        isLoading.value = false;
        return;
      }
      
      // Process contractor performance data
      const performanceData = contractors.map(contractor => {
        const contractorName = contractor.contractor_name || 'Unknown Contractor';
        // Prioritize scope_detail (actual description from G702/G703) over dda_category
        const scopeDetail = contractor.scope_detail || contractor.dda_term || contractor.dda_category || 'General Work';
        
        // Financial data from Sub G702
        const originalBudget = parseFloat(contractor.original_contract_sum || 0);
        const currentBudget = parseFloat(contractor.contract_sum_to_date || 0);
        const totalBilled = parseFloat(contractor.total_completed_and_stored_to_date || 0);
        
        // Budget variance = current budget - original budget (the increase/decrease from original)
        const budgetVariance = currentBudget - originalBudget;
        // Variance percent = (current - original) / original * 100
        const budgetVariancePct = originalBudget > 0 ? (budgetVariance / originalBudget) * 100 : 0;
        
        return {
          company_name: contractorName,
          scope_of_work: scopeDetail,  // Use actual scope/description from G702/G703
          original_budget: originalBudget,
          current_budget: currentBudget,
          budget_increases: currentBudget - originalBudget,
          actual_duration: 0, // Not available
          projected_duration: 0, // Not available
          budget_variance: budgetVariance,
          budget_variance_percent: budgetVariancePct / 100,
          schedule_variance: 0, // Not available
          schedule_variance_percent: 0, // Not available
          project_id: contractor.project_id,
          draw_number: contractor.draw_number,
          application_number: contractor.application_number
        };
      });
      
      // Sort by budget descending
      subcontractors.value = performanceData.sort((a, b) => b.current_budget - a.current_budget);
      
      // Extract unique scopes of work for the filter dropdown
      const scopes = new Set();
      performanceData.forEach(s => scopes.add(s.scope_of_work));
      availableScopes.value = Array.from(scopes);
      
      console.log('✅ Processed contractor performance data:', subcontractors.value.length, 'contractors');
      isLoading.value = false;
    },
    errorStore
  );
}

// Component lifecycle
onMounted(() => {
  authToken.value = authStore.getAuthTokenHeader;
  fetchSubcontractorData();
  
  // Subscribe to changes in the comp set store
  compSetStore.$subscribe(() => {
    fetchSubcontractorData();
  });
});
</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

.companies-container {
  padding: 20px 15px;
}

/* Variance Charts Section */
.variance-charts-section {
  margin-bottom: 30px;
}

.variance-charts-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.variance-chart-card {
  background: linear-gradient(135deg, #1a2a3a 0%, #0d1821 100%);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  h3 {
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 20px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
}

.variance-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.variance-bar-item {
  display: grid;
  grid-template-columns: 120px 1fr 80px;
  gap: 10px;
  align-items: center;
}

.bar-label {
  font-size: 12px;
  color: #a0aec0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-container {
  display: flex;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.bar-original {
  background: $brightBlue1;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  min-width: 60px;
}

.bar-variance {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: white;
  min-width: 50px;
  
  &.positive {
    background: linear-gradient(90deg, #48bb78 0%, #38a169 100%);
  }
  
  &.negative {
    background: linear-gradient(90deg, #fc8181 0%, #e53e3e 100%);
  }
}

.bar-total {
  font-size: 12px;
  color: #fff;
  font-weight: 600;
  text-align: right;
}

.company-header-container {
  margin-bottom: 20px;
}

.company-header-title {
  font-size: 22px;
  font-weight: bold;
  color: $navyBlue;
  margin-bottom: 5px;
}

.company-header-subtitle {
  font-size: 14px;
  color: $paleGray1;
  margin-bottom: 15px;
}

.company-search-controls {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.company-search-input, .scope-filter, .sort-order {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.company-search-input {
  flex: 2;
}

.scope-filter, .sort-order {
  flex: 1;
}

.loading-container, .no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: $paleGray1;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid $brightBlue1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.football-field-charts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.subcontractor-chart {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 15px;
}

.subcontractor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  
  h3 {
    margin: 0;
    font-size: 18px;
  }
}

.scope-tag {
  background-color: $lightBlue1;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.variance-chart {
  margin-bottom: 15px;
}

.chart-label {
  font-size: 14px;
  margin-bottom: 5px;
}

.football-field {
  display: flex;
  height: 30px;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.original-value {
  background-color: $brightBlue1;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.variance-value {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  
  &.positive {
    background-color: #4CAF50;
  }
  
  &.negative {
    background-color: #F44336;
  }
}

.contract-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 12px;
  color: $paleGray1;
}

.detail-value {
  font-size: 14px;
  font-weight: bold;
}

.no-data-message {
  text-align: center;
  padding: 40px 20px;
  
  h3 {
    color: #333;
    margin-bottom: 16px;
  }
  
  p {
    color: #666;
    margin-bottom: 16px;
    line-height: 1.5;
  }
  
  .help-text {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
    text-align: left;
    
    p {
      margin-bottom: 12px;
      font-weight: 600;
    }
    
    ol {
      margin: 0;
      padding-left: 20px;
      
      li {
        margin-bottom: 8px;
        color: #555;
      }
    }
  }
  
  .note {
    font-style: italic;
    color: #888;
    margin-top: 20px;
  }
}
</style>
